import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { AuthShell } from "./AuthShell";
import { useAuth } from "@/lib/auth";
// Firebase email verification replaced by backend PHP logic
import { Loader2, Check, MailCheck } from "lucide-react";
import { toast } from "sonner";
import { useGoogleLogin } from "@react-oauth/google";

export default function Register() {
    const navigate = useNavigate();
    const { register, loginWithGoogle, getErrorMessage } = useAuth();
    const [name, setName] = useState("");
    const [email, setEmail] = useState("");
    const [password, setPassword] = useState("");
    const [agree, setAgree] = useState(true);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState("");
    const [verificationSent, setVerificationSent] = useState(false);

    const loginGoogle = useGoogleLogin({
        onSuccess: async (res) => {
            setError(""); setLoading(true);
            try {
                await loginWithGoogle(res.access_token);
                toast.success("Account created successfully!");
                navigate("/dashboard", { replace: true });
            } catch (err) {
                setError(getErrorMessage(err));
            } finally {
                setLoading(false);
            }
        },
        onError: () => setError("Google Sign-In failed"),
    });

    const onSubmit = async (e) => {
        e.preventDefault();
        setError(""); setLoading(true);
        try {
            await register(name, email, password);
            setVerificationSent(true);
        } catch (err) {
            setError(getErrorMessage(err));
        } finally {
            setLoading(false);
        }
    };

    if (verificationSent) {
        return (
            <AuthShell title="Check your inbox." subtitle="One last step before you start.">
                <div className="bg-mint/20 border border-mint/40 rounded-3xl p-8 text-center space-y-4">
                    <MailCheck className="mx-auto text-ink" size={36}/>
                    <p className="text-[15px] text-ink leading-relaxed">
                        We sent a verification link to <span className="font-semibold">{email}</span>.
                    </p>
                    <p className="text-[13px] text-ink-muted">
                        Click the link in the email to verify your account, then sign in.
                        <br/><span className="text-coral font-medium mt-1 inline-block">Please check your spam folder if you don't see it!</span>
                    </p>
                    <button
                        onClick={() => navigate("/login", { replace: true })}
                        className="mt-2 w-full bg-ink hover:bg-coral text-cream-100 rounded-full py-3.5 text-[14.5px] font-semibold transition-all"
                    >
                        Go to Sign in →
                    </button>
                </div>
            </AuthShell>
        );
    }

    return (
        <AuthShell
            title="Start your free account."
            subtitle="20 articles a month. No credit card. Cancel anytime."
            side="left"
        >
            <form onSubmit={onSubmit} data-testid="register-form" className="space-y-4">
                <button
                    type="button"
                    onClick={() => loginGoogle()}
                    disabled={loading}
                    className="w-full bg-white hover:bg-cream-100 border border-cream-300 text-ink rounded-full py-3.5 text-[14.5px] font-semibold inline-flex items-center justify-center gap-2 transition-all disabled:opacity-70 mb-4"
                >
                    <svg className="w-5 h-5" viewBox="0 0 24 24">
                        <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" />
                        <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" />
                        <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" />
                        <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" />
                    </svg>
                    Continue with Google
                </button>

                <div className="relative flex items-center py-2 mb-4">
                    <div className="flex-grow border-t border-cream-300"></div>
                    <span className="flex-shrink-0 mx-4 text-ink-muted text-[11px] uppercase tracking-wider font-semibold">Or with email</span>
                    <div className="flex-grow border-t border-cream-300"></div>
                </div>

                <div>
                    <label className="block text-[12px] font-semibold text-ink mb-1.5">Your name</label>
                    <input
                        type="text" required value={name} onChange={(e) => setName(e.target.value)}
                        data-testid="register-name" placeholder="Editor's name"
                        className="w-full bg-white border border-cream-300 rounded-2xl px-4 py-3 text-[15px] focus:outline-none focus:ring-2 focus:ring-coral/40 focus:border-coral transition"
                    />
                </div>
                <div>
                    <label className="block text-[12px] font-semibold text-ink mb-1.5">Work email</label>
                    <input
                        type="email" required value={email} onChange={(e) => setEmail(e.target.value)}
                        data-testid="register-email" placeholder="you@newsroom.com"
                        className="w-full bg-white border border-cream-300 rounded-2xl px-4 py-3 text-[15px] focus:outline-none focus:ring-2 focus:ring-coral/40 focus:border-coral transition"
                    />
                </div>
                <div>
                    <label className="block text-[12px] font-semibold text-ink mb-1.5">Password</label>
                    <input
                        type="password" required minLength={6} value={password} onChange={(e) => setPassword(e.target.value)}
                        data-testid="register-password" placeholder="At least 6 characters"
                        className="w-full bg-white border border-cream-300 rounded-2xl px-4 py-3 text-[15px] focus:outline-none focus:ring-2 focus:ring-coral/40 focus:border-coral transition"
                    />
                </div>
                <label className="flex items-start gap-2.5 cursor-pointer pt-1">
                    <span className={`mt-0.5 w-4 h-4 rounded-md border flex items-center justify-center transition-colors flex-shrink-0 ${agree ? "bg-ink border-ink" : "bg-white border-cream-300"}`}>
                        {agree && <Check size={11} className="text-cream-100" strokeWidth={3.5}/>}
                    </span>
                    <input type="checkbox" checked={agree} onChange={() => setAgree(!agree)} className="sr-only"/>
                    <span className="text-[12.5px] text-ink-muted leading-relaxed">
                        I agree to the <a href="#" className="text-ink font-semibold hover:text-coral">Terms</a> and <a href="#" className="text-ink font-semibold hover:text-coral">Privacy Policy</a>.
                    </span>
                </label>
                {error && <p className="text-[13px] text-coral font-medium" data-testid="register-error">{error}</p>}
                <button
                    type="submit" disabled={loading || !agree} data-testid="register-submit"
                    className="w-full bg-ink hover:bg-coral text-cream-100 rounded-full py-3.5 text-[14.5px] font-semibold inline-flex items-center justify-center gap-2 transition-all disabled:opacity-70"
                >
                    {loading ? <><Loader2 size={16} className="animate-spin"/> Creating account…</> : <>Create account →</>}
                </button>
                <p className="text-[13px] text-ink-muted text-center pt-2">
                    Already have an account? <Link to="/login" className="text-ink font-semibold hover:text-coral" data-testid="link-login">Sign in</Link>
                </p>
            </form>
        </AuthShell>
    );
}
